import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  Avatar,
  Divider,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
  Chip,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import SaveIcon from "@material-ui/icons/Save";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import CheckIcon from "@material-ui/icons/Check";
import CancelIcon from "@material-ui/icons/Cancel";
import NoteIcon from "@material-ui/icons/Notes";
import AssignmentIcon from "@material-ui/icons/Assignment";
import LabelIcon from "@material-ui/icons/Label";
import AlarmIcon from "@material-ui/icons/Alarm";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import WifiIcon from "@material-ui/icons/Wifi";
import LocalHospitalIcon from "@material-ui/icons/LocalHospital";
import SearchIcon from "@material-ui/icons/Search";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import PowerIcon from "@material-ui/icons/PowerSettingsNew";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";
import { toast } from "react-toastify";
import moment from "moment";
import useTicketNotes from "../../hooks/useTicketNotes";
import ConfirmationModal from "../ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    width: 420,
    maxWidth: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#fff",
    borderLeft: "1px solid #e2e8f0",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 20px",
    borderBottom: "1px solid #e2e8f0",
    background: "#f8fafc",
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: 15,
    color: "#1a202c",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  tabs: {
    borderBottom: "1px solid #e2e8f0",
    minHeight: 40,
    background: "#f8fafc",
  },
  tab: {
    minHeight: 40,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "none",
    minWidth: 0,
    flex: 1,
    padding: "6px 8px",
  },
  tabPanel: {
    flex: 1,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
  },
  addSection: {
    padding: "14px 16px",
    borderBottom: "1px solid #e2e8f0",
    background: "#f8fafc",
  },
  noteItem: {
    padding: 0,
    borderBottom: "1px solid #f1f5f9",
    "&:hover": { background: "#f8fafc" },
  },
  noteText: {
    fontSize: 13,
    color: "#2d3748",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  noteMeta: {
    fontSize: 11,
    color: "#718096",
    marginTop: 2,
  },
  avatar: {
    width: 32,
    height: 32,
    fontSize: 12,
    backgroundColor: "#4299e1",
    flexShrink: 0,
  },
  avatarIa: {
    width: 32,
    height: 32,
    fontSize: 11,
    backgroundColor: "#805ad5",
    flexShrink: 0,
  },
  emptyState: {
    textAlign: "center",
    color: "#a0aec0",
    padding: "32px 20px",
    fontSize: 13,
  },
  // Protocolo
  protocolItem: {
    display: "flex",
    flexDirection: "column",
    padding: "12px 16px",
    borderBottom: "1px solid #f1f5f9",
    "&:hover": { background: "#f8fafc" },
    cursor: "default",
  },
  protocolId: {
    fontWeight: 700,
    fontSize: 13,
    color: "#2d3748",
  },
  protocolMeta: {
    fontSize: 11,
    color: "#718096",
    marginTop: 2,
  },
  statusChip: {
    height: 18,
    fontSize: 10,
    fontWeight: 600,
    marginLeft: 8,
  },
  // SGP
  sgpCard: {
    margin: "12px 16px 0",
    borderRadius: 8,
    border: "1px solid #e2e8f0",
    overflow: "hidden",
  },
  sgpCardHeader: {
    padding: "10px 14px",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  sgpCardBody: {
    padding: "10px 14px",
    borderTop: "1px solid #e2e8f0",
  },
  sgpFaturaItem: {
    padding: "8px 14px",
    borderTop: "1px solid #f1f5f9",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  sgpActionBtn: {
    borderRadius: 6,
    textTransform: "none",
    fontWeight: 600,
    fontSize: 13,
    padding: "7px 14px",
  },
  // Sys Clinica
  scCard: {
    margin: "12px 16px 0",
    borderRadius: 8,
    border: "1px solid #e2e8f0",
    overflow: "hidden",
  },
  scCardHeader: {
    padding: "10px 14px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "#d1fae5",
  },
  scCardBody: {
    padding: "10px 14px",
    borderTop: "1px solid #e2e8f0",
  },
  scActionBtn: {
    borderRadius: 6,
    textTransform: "none",
    fontWeight: 600,
    fontSize: 13,
    padding: "7px 14px",
  },
  // Tags
  tagSection: {
    padding: "16px",
  },
  tagSectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: "#4a5568",
    marginBottom: 8,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  tagChip: {
    margin: "2px 4px 2px 0",
    fontSize: 12,
  },
}));

const STATUS_LABELS = {
  open: { label: "Atendendo", color: "#38a169" },
  pending: { label: "Aguardando", color: "#d69e2e" },
  closed: { label: "Finalizado", color: "#718096" },
};

export default function TicketNotesDrawer({ open, onClose, ticket }) {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { saveNote, deleteNote, listNotes } = useTicketNotes();

  const [tab, setTab] = useState(0);

  // Aba Anotações
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [hasReminder, setHasReminder] = useState(false);
  const [reminderAt, setReminderAt] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  // Aba Protocolos
  const [protocols, setProtocols] = useState([]);
  const [loadingProtocols, setLoadingProtocols] = useState(false);

  // Aba Tags
  const [tagHistory, setTagHistory] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);

  // Aba SGP — visível só se integração estiver ativa
  const [sgpAtivo, setSgpAtivo] = useState(false);
  const [sgpCpf, setSgpCpf] = useState("");
  const [sgpData, setSgpData] = useState(null);
  const [sgpLoading, setSgpLoading] = useState(false);
  const [sgpError, setSgpError] = useState("");
  const [sgpChamadoOpen, setSgpChamadoOpen] = useState(false);
  const [sgpChamadoContrato, setSgpChamadoContrato] = useState("");
  const [sgpChamadoTexto, setSgpChamadoTexto] = useState("");
  const [sgpChamadoLoading, setSgpChamadoLoading] = useState(false);
  const [sgpReligacaoOpen, setSgpReligacaoOpen] = useState(false);
  const [sgpReligacaoContrato, setSgpReligacaoContrato] = useState("");
  const [sgpReligacaoLoading, setSgpReligacaoLoading] = useState(false);

  // Aba Sys Clinica — visível só se integração estiver ativa
  const [scAtivo, setScAtivo] = useState(false);
  const [scCpf, setScCpf] = useState("");
  const [scPaciente, setScPaciente] = useState(null);
  const [scLoading, setScLoading] = useState(false);
  const [scError, setScError] = useState("");
  const [scModalType, setScModalType] = useState(null);
  const [scModalData, setScModalData] = useState(null);
  const [scModalLoading, setScModalLoading] = useState(false);
  const [scModalError, setScModalError] = useState("");

  // CRUD agendamentos
  const [scAgendForm, setScAgendForm] = useState(null); // null = fechado, {} = novo, {id,...} = edição
  const [scAgendSaving, setScAgendSaving] = useState(false);
  const [scFuncionarios, setScFuncionarios] = useState([]);
  const [scServicos, setScServicos] = useState([]);
  const [scServicosLoading, setScServicosLoading] = useState(false);

  // Cronograma
  const [scCronogramaForm, setScCronogramaForm] = useState(null); // null = fechado
  const [scTreinosLista, setScTreinosLista] = useState([]);
  const [scCronogramaSaving, setScCronogramaSaving] = useState(false);

  const SC_DIAS = ["segunda", "terca", "quarta", "quinta", "sexta", "sabado", "domingo"];
  const SC_DIAS_LABEL = {
    segunda: "Segunda-feira", terca: "Terça-feira", quarta: "Quarta-feira",
    quinta: "Quinta-feira", sexta: "Sexta-feira", sabado: "Sábado", domingo: "Domingo",
  };

  const isAdminOrManager =
    user?.profile === "admin" ||
    user?.userType === "administrador" ||
    user?.userType === "gerente";

  // Todos os users têm profile="admin" neste sistema — usar userType para SC
  const scIsAdminOrManager =
    user?.userType === "admin" ||
    user?.userType === "administrador" ||
    user?.userType === "manager" ||
    user?.userType === "gerente";

  const scPerm = {
    verDados:           scIsAdminOrManager || user?.scVerDados === "enabled",
    verAgendamentos:    scIsAdminOrManager || user?.scVerAgendamentos === "enabled",
    editarAgendamentos: scIsAdminOrManager || user?.scEditarAgendamentos === "enabled",
    verTreinos:         scIsAdminOrManager || user?.scVerTreinos === "enabled",
    editarCronograma:   scIsAdminOrManager || user?.scEditarCronograma === "enabled",
    verAnamnese:        scIsAdminOrManager || user?.scVerAnamnese === "enabled",
    verHistorico:       scIsAdminOrManager || user?.scVerHistorico === "enabled",
    verContas:          scIsAdminOrManager || user?.scVerContas === "enabled",
    verTratamentos:     scIsAdminOrManager || user?.scVerTratamentos === "enabled",
  };

  useEffect(() => {
    if (open && ticket?.id) {
      loadNotes();
      loadProtocols();
      loadTagHistory();
      api.get("/sgp-integration")
        .then(({ data }) => setSgpAtivo(!!(data && data.ativo)))
        .catch(() => setSgpAtivo(false));
      api.get("/sys-clinica-integration")
        .then(({ data }) => setScAtivo(!!(data && data.ativo)))
        .catch(() => setScAtivo(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ticket?.id]);

  const loadNotes = async () => {
    setLoadingNotes(true);
    try {
      const data = await listNotes({ ticketId: ticket.id, contactId: ticket.contactId });
      setNotes(data);
    } catch {
      toast.error("Erro ao carregar anotações");
    }
    setLoadingNotes(false);
  };

  const loadProtocols = async () => {
    setLoadingProtocols(true);
    try {
      const { data } = await api.get("/tickets", {
        params: { contactId: ticket.contactId, pageNumber: 1, status: "all" },
      });
      setProtocols(data.tickets || []);
    } catch {
      // silencioso
    }
    setLoadingProtocols(false);
  };

  const loadTagHistory = async () => {
    setLoadingTags(true);
    try {
      const { data } = await api.get(`/tickets-tag-history/${ticket.id}`);
      setTagHistory(data);
    } catch {
      // silencioso
    }
    setLoadingTags(false);
  };

  // ── SGP helpers ─────────────────────────────────────────────────────────────

  const sgpBuscarCliente = useCallback(async (cpfParam) => {
    const cpf = (cpfParam || sgpCpf).replace(/\D/g, "");
    if (!cpf || cpf.length < 11) {
      setSgpError("Informe um CPF ou CNPJ válido.");
      return;
    }
    setSgpLoading(true);
    setSgpError("");
    setSgpData(null);
    try {
      const { data } = await api.get("/sgp/cliente", { params: { cpf } });
      setSgpData(data);
      // pré-seleciona o primeiro contrato para os modais
      if (data.contratos?.[0]) {
        setSgpChamadoContrato(String(data.contratos[0].id));
        setSgpReligacaoContrato(String(data.contratos[0].id));
      }
      // Persistir no contato, lead e cliente CRM (não bloqueia a UI se falhar)
      api.post("/sgp/salvar-consulta", {
        ticketId: ticket.id,
        cpf,
        clienteId: data.id,
      }).catch(() => {});
    } catch (e) {
      setSgpError(e?.response?.data?.error || "Cliente não encontrado no SGP.");
    }
    setSgpLoading(false);
  }, [sgpCpf, ticket?.id]);

  // Ao abrir a aba SGP: tenta carregar automaticamente de 3 fontes (em ordem de prioridade)
  //   1. ticket.dataWebhook.variables.sgp_cpf  — gravado pelo flow ou por consulta anterior
  //   2. ticket.contact.cpfCnpj               — gravado por salvarConsulta no contato
  //   3. ticket.dataWebhook.sgpDiagnosticoState.cpf — legado
  const sgpAutoLoad = useCallback(() => {
    if (sgpData || sgpLoading) return;
    const cpfDoFlow =
      ticket?.dataWebhook?.variables?.sgp_cpf ||
      ticket?.contact?.cpfCnpj ||
      ticket?.dataWebhook?.sgpDiagnosticoState?.cpf;
    if (cpfDoFlow) {
      const cpfLimpo = cpfDoFlow.replace(/\D/g, "");
      setSgpCpf(cpfLimpo);
      sgpBuscarCliente(cpfLimpo);
    }
  }, [ticket, sgpData, sgpLoading, sgpBuscarCliente]);

  const handleSgpAbrirChamado = async () => {
    if (!sgpChamadoTexto.trim()) {
      toast.error("Descreva o problema.");
      return;
    }
    setSgpChamadoLoading(true);
    try {
      const { data } = await api.post("/sgp/chamado", {
        contrato: sgpChamadoContrato,
        conteudo: sgpChamadoTexto.trim(),
      });
      toast.success(`Chamado aberto! Protocolo: ${data.protocolo || data.id}`);
      setSgpChamadoOpen(false);
      setSgpChamadoTexto("");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao abrir chamado.");
    }
    setSgpChamadoLoading(false);
  };

  const handleSgpReligacao = async () => {
    setSgpReligacaoLoading(true);
    try {
      await api.post("/sgp/religacao-confianca", { contrato: sgpReligacaoContrato });
      toast.success("Religação de confiança realizada com sucesso!");
      setSgpReligacaoOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao realizar religação.");
    }
    setSgpReligacaoLoading(false);
  };

  const copiarTexto = (texto) => {
    navigator.clipboard.writeText(texto).then(() => toast.success("Copiado!"));
  };

  // ── fim SGP helpers ──────────────────────────────────────────────────────────

  // ── Sys Clinica helpers ──────────────────────────────────────────────────────

  const scTabIdx = sgpAtivo ? 4 : 3;

  const scBuscarPaciente = useCallback(async (cpfParam) => {
    const cpf = (cpfParam || scCpf).replace(/\D/g, "");
    if (!cpf || cpf.length < 11) {
      setScError("Informe um CPF válido (11 dígitos).");
      return;
    }
    setScLoading(true);
    setScError("");
    setScPaciente(null);
    try {
      const { data } = await api.get("/sys-clinica/paciente", { params: { cpf } });
      setScPaciente(data?.paciente || null);
      if (!data?.paciente) setScError("Paciente não encontrado no Sys Clinica.");
    } catch (e) {
      setScError(e?.response?.data?.message || "Paciente não encontrado no Sys Clinica.");
    }
    setScLoading(false);
  }, [scCpf]);

  const scAutoLoad = useCallback(() => {
    if (scPaciente || scLoading) return;
    const cpf = ticket?.contact?.cpfCnpj;
    if (cpf) {
      const cpfLimpo = cpf.replace(/\D/g, "");
      setScCpf(cpfLimpo);
      scBuscarPaciente(cpfLimpo);
    }
  }, [ticket, scPaciente, scLoading, scBuscarPaciente]);

  const scAbrirModal = async (tipo) => {
    setScModalType(tipo);
    setScModalData(null);
    setScModalError("");
    setScModalLoading(true);
    const cpf = scPaciente?.cpf?.replace(/\D/g, "") || scCpf.replace(/\D/g, "");
    const endpointMap = {
      agendamentos: { path: "/sys-clinica/agendamentos", params: { cpf, tipo: "futuros" } },
      historico:    { path: "/sys-clinica/agendamentos", params: { cpf, tipo: "passados" } },
      treinos:      { path: "/sys-clinica/treinos",      params: { cpf } },
      anamnese:     { path: "/sys-clinica/anamnese",     params: { cpf } },
      contas:       { path: "/sys-clinica/receber",      params: { cpf, status: "todos" } },
      tratamentos:  { path: "/sys-clinica/tratamentos",  params: { cpf } },
    };
    const endpoint = endpointMap[tipo];
    try {
      const { data } = await api.get(endpoint.path, { params: endpoint.params });
      setScModalData(data);
    } catch (e) {
      setScModalError(e?.response?.data?.message || "Erro ao carregar dados.");
    }
    setScModalLoading(false);
  };

  const scAbrirNovoAgendamento = async () => {
    setScAgendForm({ data: "", hora: "", funcionario_id: "", servico_id: "", status: "Aguardando", pago: "Não", obs: "" });
    if (!scFuncionarios.length) {
      const { data } = await api.get("/sys-clinica/funcionarios");
      setScFuncionarios(data?.funcionarios || []);
    }
  };

  const scAbrirEditarAgendamento = async (ag) => {
    setScAgendForm({ id: ag.id, data: ag.data || "", hora: ag.hora?.slice(0, 5) || "", funcionario_id: ag.funcionario_id || "", servico_id: ag.servico_id || "", status: ag.status || "Aguardando", pago: ag.pago || "Não", obs: ag.obs || "" });
    if (!scFuncionarios.length) {
      const { data } = await api.get("/sys-clinica/funcionarios");
      setScFuncionarios(data?.funcionarios || []);
    }
    if (ag.funcionario_id) {
      setScServicosLoading(true);
      const { data } = await api.get("/sys-clinica/servicos", { params: { funcionario_id: ag.funcionario_id } });
      setScServicos(data?.servicos || []);
      setScServicosLoading(false);
    }
  };

  const scOnFuncionarioChange = async (funcionarioId) => {
    setScAgendForm(prev => ({ ...prev, funcionario_id: funcionarioId, servico_id: "" }));
    if (!funcionarioId) { setScServicos([]); return; }
    setScServicosLoading(true);
    const { data } = await api.get("/sys-clinica/servicos", { params: { funcionario_id: funcionarioId } });
    setScServicos(data?.servicos || []);
    setScServicosLoading(false);
  };

  const scSalvarAgendamento = async () => {
    const cpf = scPaciente?.cpf?.replace(/\D/g, "") || scCpf.replace(/\D/g, "");
    if (!scAgendForm.data || !scAgendForm.hora || !scAgendForm.funcionario_id || !scAgendForm.servico_id) {
      toast.error("Preencha data, hora, profissional e serviço.");
      return;
    }
    setScAgendSaving(true);
    try {
      const body = {
        paciente: scPaciente?.id,
        funcionario: Number(scAgendForm.funcionario_id),
        servico: Number(scAgendForm.servico_id),
        data: scAgendForm.data,
        hora: scAgendForm.hora,
        status: scAgendForm.status,
        pago: scAgendForm.pago,
        obs: scAgendForm.obs,
      };
      if (scAgendForm.id) {
        await api.put(`/sys-clinica/agendamentos-crud/${scAgendForm.id}`, body);
        toast.success("Agendamento atualizado!");
      } else {
        await api.post("/sys-clinica/agendamentos-crud", body);
        toast.success("Agendamento criado!");
      }
      setScAgendForm(null);
      scAbrirModal("agendamentos");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Erro ao salvar agendamento.");
    }
    setScAgendSaving(false);
  };

  const scCancelarAgendamento = async (id) => {
    if (!window.confirm("Confirmar cancelamento do agendamento?")) return;
    try {
      await api.delete(`/sys-clinica/agendamentos-crud/${id}`);
      toast.success("Agendamento cancelado.");
      scAbrirModal("agendamentos");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Erro ao cancelar agendamento.");
    }
  };

  const scAbrirCronogramaForm = async () => {
    setScCronogramaSaving(false);
    const [listRes, cronRes] = await Promise.all([
      api.get("/sys-clinica/treinos-lista"),
      api.get("/sys-clinica/cronograma", { params: { cpf: scPaciente?.cpf?.replace(/\D/g, "") || scCpf.replace(/\D/g, "") } }),
    ]);
    setScTreinosLista(listRes.data?.treinos || []);
    const dias = SC_DIAS.reduce((acc, d) => {
      const dia = cronRes.data?.cronograma?.dias?.[d];
      acc[d] = { treino_id: dia?.treino_id || "", obs: dia?.obs || "" };
      return acc;
    }, {});
    setScCronogramaForm({ nome: cronRes.data?.cronograma?.nome || "", ...dias });
  };

  const scSalvarCronograma = async () => {
    const cpf = scPaciente?.cpf?.replace(/\D/g, "") || scCpf.replace(/\D/g, "");
    setScCronogramaSaving(true);
    try {
      const body = { nome: scCronogramaForm.nome };
      SC_DIAS.forEach(d => {
        body[d] = { treino_id: scCronogramaForm[d]?.treino_id || null, obs: scCronogramaForm[d]?.obs || null };
      });
      await api.post("/sys-clinica/cronograma", body, { params: { cpf } });
      toast.success("Cronograma salvo com sucesso!");
      setScCronogramaForm(null);
      scAbrirModal("treinos");
    } catch (e) {
      toast.error(e?.response?.data?.mensagem || "Erro ao salvar cronograma.");
    }
    setScCronogramaSaving(false);
  };

  // ── fim Sys Clinica helpers ───────────────────────────────────────────────────

  const handleSave = async () => {
    if (!newNote.trim()) return;
    if (hasReminder && !reminderAt) {
      toast.error("Informe a data e hora do lembrete");
      return;
    }
    setSaving(true);
    try {
      await saveNote({
        note: newNote.trim(),
        ticketId: ticket.id,
        contactId: ticket.contactId,
        reminderAt: hasReminder && reminderAt ? reminderAt : null,
      });
      setNewNote("");
      setHasReminder(false);
      setReminderAt("");
      await loadNotes();
      toast.success(hasReminder ? "Anotação com lembrete adicionada!" : "Anotação adicionada!");
    } catch {
      toast.error("Erro ao salvar anotação");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await deleteNote(deleteTarget.id);
      await loadNotes();
      toast.success("Anotação excluída!");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Sem permissão para excluir");
    }
    setDeleteTarget(null);
  };

  const handleEditSave = async (note) => {
    if (!editText.trim()) return;
    try {
      await api.put(`/ticket-notes/${note.id}`, { ...note, note: editText.trim() });
      await loadNotes();
      setEditingId(null);
      toast.success("Anotação atualizada!");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Sem permissão para editar");
    }
  };

  const handleDoneReminder = async (note) => {
    try {
      await api.put(`/ticket-notes/${note.id}/done`);
      await loadNotes();
      toast.success("Lembrete marcado como concluído!");
    } catch {
      toast.error("Erro ao dar baixa no lembrete");
    }
  };

  const canEdit = (note) => isAdminOrManager || note.userId === user?.id;
  const canDelete = () => isAdminOrManager;

  const getInitials = (name) =>
    (name || "?").split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase();

  return (
    <>
      <ConfirmationModal
        title="Excluir anotação"
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      >
        Deseja excluir esta anotação permanentemente?
      </ConfirmationModal>

      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        classes={{ paper: classes.drawerPaper }}
      >
        {/* Header */}
        <Box className={classes.header}>
          <Typography className={classes.headerTitle}>
            Histórico do Atendimento
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => { setTab(v); if (v === 3 && sgpAtivo) sgpAutoLoad(); if (v === scTabIdx && scAtivo) scAutoLoad(); }}
          className={classes.tabs}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            className={classes.tab}
            icon={<NoteIcon style={{ fontSize: 14 }} />}
            label="Anotações"
          />
          <Tab
            className={classes.tab}
            icon={<AssignmentIcon style={{ fontSize: 14 }} />}
            label="Protocolos"
          />
          <Tab
            className={classes.tab}
            icon={<LabelIcon style={{ fontSize: 14 }} />}
            label="Tags"
          />
          {sgpAtivo && (
            <Tab
              className={classes.tab}
              icon={<WifiIcon style={{ fontSize: 14 }} />}
              label="SGP"
              onClick={() => tab === 3 && sgpAutoLoad()}
            />
          )}
          {scAtivo && scPerm.verDados && (
            <Tab
              className={classes.tab}
              icon={<LocalHospitalIcon style={{ fontSize: 14 }} />}
              label="Clínica"
              onClick={() => tab === scTabIdx && scAutoLoad()}
            />
          )}
        </Tabs>

        {/* ── ABA 0: ANOTAÇÕES ── */}
        {tab === 0 && (
          <Box className={classes.tabPanel}>
            <Box className={classes.addSection}>
              <TextField
                label="Nova anotação"
                placeholder="Ctrl+Enter para salvar..."
                multiline
                minRows={3}
                variant="outlined"
                fullWidth
                size="small"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleSave();
                }}
              />
              <Box display="flex" alignItems="center" mt={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={hasReminder}
                      onChange={(e) => {
                        setHasReminder(e.target.checked);
                        if (!e.target.checked) setReminderAt("");
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={4} style={{ gap: 4 }}>
                      <AlarmIcon style={{ fontSize: 15, color: hasReminder ? "#6366f1" : "#a0aec0" }} />
                      <Typography style={{ fontSize: 12, color: hasReminder ? "#6366f1" : "#718096" }}>
                        Adicionar lembrete
                      </Typography>
                    </Box>
                  }
                  style={{ margin: 0 }}
                />
              </Box>
              {hasReminder && (
                <Box mt={1}>
                  <TextField
                    label="Data e hora do lembrete"
                    type="datetime-local"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={reminderAt}
                    onChange={(e) => setReminderAt(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: new Date().toISOString().slice(0, 16)
                    }}
                  />
                </Box>
              )}
              <Box display="flex" justifyContent="flex-end" mt={1}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={saving ? <CircularProgress size={12} color="inherit" /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving || !newNote.trim()}
                >
                  Salvar
                </Button>
              </Box>
            </Box>

            <Box flex={1} overflow="auto">
              {loadingNotes ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress size={24} />
                </Box>
              ) : notes.length === 0 ? (
                <Typography className={classes.emptyState}>
                  Nenhuma anotação registrada ainda.
                </Typography>
              ) : (
                <List disablePadding>
                  {notes.map((note) => (
                    <React.Fragment key={note.id}>
                      <ListItem className={classes.noteItem} disableGutters>
                        <Box display="flex" alignItems="flex-start" width="100%" px={2} py={1.5}>
                          <Avatar
                            className={note.source === "ia" ? classes.avatarIa : classes.avatar}
                            style={{ marginRight: 10, marginTop: 2 }}
                          >
                            {note.source === "ia" ? "IA" : getInitials(note.user?.name)}
                          </Avatar>

                          <Box flex={1} minWidth={0}>
                            {editingId === note.id ? (
                              <TextField
                                multiline
                                minRows={3}
                                variant="outlined"
                                fullWidth
                                size="small"
                                value={editText}
                                onChange={(e) => setEditText(e.target.value)}
                                autoFocus
                              />
                            ) : (
                              <Typography className={classes.noteText}>{note.note}</Typography>
                            )}
                            <Typography className={classes.noteMeta}>
                              {note.source === "ia" ? "Inteligência Artificial" : note.user?.name} •{" "}
                              {moment(note.createdAt).format("DD/MM/YY HH:mm")}
                            </Typography>
                            {note.reminderAt && (
                              <Box display="flex" alignItems="center" style={{ gap: 4, marginTop: 4, background: note.reminderDone ? "#f0fff4" : "#f0f0ff", borderRadius: 6, padding: "3px 8px", border: `1px solid ${note.reminderDone ? "#9ae6b4" : "#c3b5fd"}` }}>
                                <AlarmIcon style={{ fontSize: 12, color: note.reminderDone ? "#38a169" : "#6366f1" }} />
                                <Typography style={{ fontSize: 11, color: note.reminderDone ? "#38a169" : "#6366f1", fontWeight: 600, flex: 1, textDecoration: note.reminderDone ? "line-through" : "none" }}>
                                  {moment(note.reminderAt).format("DD/MM/YY HH:mm")}
                                </Typography>
                                {!note.reminderDone && (
                                  <Tooltip title="Dar baixa no lembrete">
                                    <IconButton size="small" style={{ padding: 2 }} onClick={() => handleDoneReminder(note)}>
                                      <CheckCircleOutlineIcon style={{ fontSize: 14, color: "#6366f1" }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {note.reminderDone && (
                                  <CheckCircleIcon style={{ fontSize: 14, color: "#38a169" }} />
                                )}
                              </Box>
                            )}
                          </Box>

                          <Box display="flex" flexDirection="column" alignItems="center" ml={0.5} flexShrink={0}>
                            {editingId === note.id ? (
                              <>
                                <Tooltip title="Confirmar">
                                  <IconButton size="small" onClick={() => handleEditSave(note)}>
                                    <CheckIcon style={{ fontSize: 16, color: "#38a169" }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Cancelar">
                                  <IconButton size="small" onClick={() => setEditingId(null)}>
                                    <CancelIcon style={{ fontSize: 16, color: "#e53e3e" }} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <>
                                {canEdit(note) && (
                                  <Tooltip title="Editar">
                                    <IconButton
                                      size="small"
                                      onClick={() => { setEditingId(note.id); setEditText(note.note); }}
                                    >
                                      <EditIcon style={{ fontSize: 15, color: "#718096" }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {canDelete() && (
                                  <Tooltip title="Excluir">
                                    <IconButton size="small" onClick={() => setDeleteTarget(note)}>
                                      <DeleteIcon style={{ fontSize: 15, color: "#e53e3e" }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </>
                            )}
                          </Box>
                        </Box>
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        )}

        {/* ── ABA 1: PROTOCOLOS ── */}
        {tab === 1 && (
          <Box className={classes.tabPanel} overflow="auto">
            {loadingProtocols ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={24} />
              </Box>
            ) : protocols.length === 0 ? (
              <Typography className={classes.emptyState}>
                Nenhum protocolo encontrado para este contato.
              </Typography>
            ) : (
              protocols.map((p) => {
                const statusInfo = STATUS_LABELS[p.status] || { label: p.status, color: "#718096" };
                return (
                  <Box key={p.id} className={classes.protocolItem}>
                    <Box display="flex" alignItems="center">
                      <Typography className={classes.protocolId}>
                        #{String(p.id).padStart(5, "0")}
                      </Typography>
                      <Chip
                        label={statusInfo.label}
                        size="small"
                        className={classes.statusChip}
                        style={{ backgroundColor: statusInfo.color, color: "#fff" }}
                      />
                      {p.id === ticket.id && (
                        <Chip
                          label="atual"
                          size="small"
                          className={classes.statusChip}
                          style={{ backgroundColor: "#4299e1", color: "#fff", marginLeft: 4 }}
                        />
                      )}
                    </Box>
                    <Typography className={classes.protocolMeta}>
                      Aberto: {moment(p.createdAt).format("DD/MM/YY HH:mm")}
                      {p.status === "closed" && p.updatedAt && ` • Fechado: ${moment(p.updatedAt).format("DD/MM/YY HH:mm")}`}
                    </Typography>
                    {p.user && (
                      <Typography className={classes.protocolMeta}>
                        Atendente: {p.user.name}
                      </Typography>
                    )}
                    {p.queue && (
                      <Typography className={classes.protocolMeta}>
                        Fila: {p.queue.name}
                      </Typography>
                    )}
                    {p.lastMessage && (
                      <Typography className={classes.protocolMeta} style={{ fontStyle: "italic", marginTop: 2 }}>
                        "{p.lastMessage.length > 60 ? p.lastMessage.slice(0, 60) + "…" : p.lastMessage}"
                      </Typography>
                    )}
                    {/* Protocolos SGP gerados pelo Flow Builder */}
                    {(() => {
                      const vars = p.dataWebhook?.variables || {};
                      const sgpItems = [
                        vars.sgp_protocolo && { icon: "📋", label: "Chamado SGP", value: vars.sgp_protocolo },
                        vars.sgp_contrato_id && { icon: "📄", label: "Contrato", value: vars.sgp_contrato_id },
                      ].filter(Boolean);
                      if (!sgpItems.length) return null;
                      return (
                        <Box mt={1} p={1} style={{ background: "#e0f2fe", borderRadius: 6, border: "1px solid #bae6fd" }}>
                          <Typography style={{ fontSize: 11, fontWeight: 700, color: "#0369a1", marginBottom: 4 }}>
                            🔧 SGP — Suporte técnico
                          </Typography>
                          {sgpItems.map((item) => (
                            <Typography key={item.label} style={{ fontSize: 12, color: "#0c4a6e", marginBottom: 1 }}>
                              {item.icon} {item.label}:{" "}
                              <strong style={{ fontFamily: "monospace" }}>{item.value}</strong>
                            </Typography>
                          ))}
                        </Box>
                      );
                    })()}
                    <Divider style={{ marginTop: 8 }} />
                  </Box>
                );
              })
            )}
          </Box>
        )}

        {/* ── ABA 2: HISTÓRICO DE TAGS ── */}
        {tab === 2 && (
          <Box className={classes.tabPanel} overflow="auto">
            {loadingTags ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={24} />
              </Box>
            ) : tagHistory.length === 0 ? (
              <Typography className={classes.emptyState}>
                Nenhuma movimentação de tag registrada.
              </Typography>
            ) : (
              <List disablePadding>
                {tagHistory.map((h) => (
                  <React.Fragment key={h.id}>
                    <ListItem disableGutters style={{ padding: "10px 16px" }}>
                      <Box display="flex" alignItems="center" width="100%" gap={1}>
                        {/* Ícone de ação */}
                        <Box
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            flexShrink: 0,
                            backgroundColor: h.action === "added" ? "#38a169" : "#e53e3e",
                          }}
                        />
                        {/* Tag */}
                        <Chip
                          label={h.tag?.name || "Tag removida"}
                          size="small"
                          style={{
                            backgroundColor: h.tag?.color || "#718096",
                            color: "#fff",
                            fontSize: 11,
                            height: 20,
                            opacity: h.action === "removed" ? 0.6 : 1,
                          }}
                        />
                        {/* Tipo kanban */}
                        {h.tag?.kanban === 1 && (
                          <Typography style={{ fontSize: 10, color: "#805ad5", fontWeight: 700 }}>
                            KANBAN
                          </Typography>
                        )}
                        {/* Ação */}
                        <Typography style={{ fontSize: 11, color: h.action === "added" ? "#38a169" : "#e53e3e", fontWeight: 600, marginLeft: 2 }}>
                          {h.action === "added" ? "adicionada" : "removida"}
                        </Typography>
                        <Box flex={1} />
                        {/* Data */}
                        <Typography style={{ fontSize: 11, color: "#a0aec0", whiteSpace: "nowrap" }}>
                          {moment(h.createdAt).format("DD/MM/YY HH:mm")}
                        </Typography>
                      </Box>
                      {h.user && (
                        <Typography style={{ fontSize: 11, color: "#718096", marginLeft: 17, marginTop: 2 }}>
                          por {h.user.name}
                        </Typography>
                      )}
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        )}
        {/* ── ABA 3: SGP CLIENTE ── */}
        {tab === 3 && sgpAtivo && (
          <Box className={classes.tabPanel} overflow="auto" pb={2}>

            {/* Busca por CPF */}
            {!sgpData && (
              <Box p={2}>
                <Typography style={{ fontSize: 12, fontWeight: 700, color: "#4a5568", marginBottom: 8 }}>
                  Consultar cliente no SGP
                </Typography>
                <Box display="flex" style={{ gap: 8 }}>
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="CPF ou CNPJ (só números)"
                    fullWidth
                    value={sgpCpf}
                    onChange={(e) => setSgpCpf(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sgpBuscarCliente()}
                    inputProps={{ maxLength: 18 }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => sgpBuscarCliente()}
                    disabled={sgpLoading}
                    style={{ whiteSpace: "nowrap", minWidth: 40 }}
                  >
                    {sgpLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon fontSize="small" />}
                  </Button>
                </Box>
                {sgpError && (
                  <Typography style={{ fontSize: 12, color: "#e53e3e", marginTop: 8 }}>
                    {sgpError}
                  </Typography>
                )}
              </Box>
            )}

            {/* Dados do cliente */}
            {sgpData && (
              <>
                {/* Card cliente */}
                <Box className={classes.sgpCard} style={{ borderColor: "#0891b2" }}>
                  <Box className={classes.sgpCardHeader} style={{ background: "#ecfeff" }}>
                    <WifiIcon style={{ fontSize: 18, color: "#0891b2" }} />
                    <Box flex={1}>
                      <Typography style={{ fontWeight: 700, fontSize: 14, color: "#164e63" }}>
                        {sgpData.nome}
                      </Typography>
                      <Typography style={{ fontSize: 11, color: "#0e7490" }}>
                        CPF/CNPJ: {sgpData.cpfcnpj}
                      </Typography>
                    </Box>
                    <Tooltip title="Nova consulta">
                      <IconButton size="small" onClick={() => { setSgpData(null); setSgpCpf(""); setSgpError(""); }}>
                        <SearchIcon style={{ fontSize: 15, color: "#64748b" }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {/* Contratos */}
                  {sgpData.contratos?.map((c) => {
                    const ativo = c.status?.toLowerCase() === "ativo";
                    const servico = c.servicos?.[0];
                    return (
                      <Box key={c.id} className={classes.sgpCardBody}>
                        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                          <Typography style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>
                            Contrato #{c.id}
                          </Typography>
                          <Chip
                            label={c.status || "—"}
                            size="small"
                            style={{
                              height: 18,
                              fontSize: 10,
                              fontWeight: 700,
                              backgroundColor: ativo ? "#d1fae5" : "#fee2e2",
                              color: ativo ? "#065f46" : "#991b1b",
                            }}
                          />
                        </Box>
                        {servico && (
                          <Typography style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                            {servico.plano?.descricao || "—"}
                            {servico.login ? ` · ${servico.login}` : ""}
                          </Typography>
                        )}
                        {c.motivo_status && (
                          <Typography style={{ fontSize: 11, color: "#b45309", marginTop: 2 }}>
                            ⚠️ {c.motivo_status}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>

                {/* Card faturas atrasadas */}
                {sgpData.faturasAtrasadas?.length > 0 && (
                  <Box className={classes.sgpCard} style={{ borderColor: "#f59e0b", marginTop: 12 }}>
                    <Box className={classes.sgpCardHeader} style={{ background: "#fffbeb" }}>
                      <ReportProblemIcon style={{ fontSize: 18, color: "#d97706" }} />
                      <Typography style={{ fontWeight: 700, fontSize: 13, color: "#92400e" }}>
                        {sgpData.totalAtrasados} fatura{sgpData.totalAtrasados !== 1 ? "s" : ""} em atraso
                        {sgpData.totalAtrasados > 3 ? " (3 mais antigas)" : ""}
                      </Typography>
                    </Box>
                    {sgpData.faturasAtrasadas.map((f) => (
                      <Box key={f.id} className={classes.sgpFaturaItem}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography style={{ fontSize: 13, fontWeight: 700, color: "#1f2937" }}>
                            R$ {parseFloat(f.valorCorrigido || f.valor || "0").toFixed(2).replace(".", ",")}
                          </Typography>
                          <Chip
                            label={`${f.diasAtraso || 0} dias`}
                            size="small"
                            style={{
                              height: 18,
                              fontSize: 10,
                              fontWeight: 700,
                              backgroundColor: (f.diasAtraso || 0) > 30 ? "#fee2e2" : "#fef9c3",
                              color: (f.diasAtraso || 0) > 30 ? "#991b1b" : "#713f12",
                            }}
                          />
                        </Box>
                        <Typography style={{ fontSize: 11, color: "#6b7280" }}>
                          Vencimento: {f.dataVencimento ? new Date(f.dataVencimento).toLocaleDateString("pt-BR") : "—"}
                          {f.status ? ` · ${f.status}` : ""}
                        </Typography>
                        <Box display="flex" style={{ gap: 6, marginTop: 4 }}>
                          {f.codigoPix && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<FileCopyIcon style={{ fontSize: 12 }} />}
                              onClick={() => copiarTexto(f.codigoPix)}
                              style={{ fontSize: 11, padding: "2px 8px", textTransform: "none", borderColor: "#059669", color: "#059669" }}
                            >
                              Copiar PIX
                            </Button>
                          )}
                          {f.link && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<OpenInNewIcon style={{ fontSize: 12 }} />}
                              onClick={() => window.open(f.link, "_blank")}
                              style={{ fontSize: 11, padding: "2px 8px", textTransform: "none", borderColor: "#2563eb", color: "#2563eb" }}
                            >
                              Boleto
                            </Button>
                          )}
                          {f.linhaDigitavel && !f.link && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<FileCopyIcon style={{ fontSize: 12 }} />}
                              onClick={() => copiarTexto(f.linhaDigitavel)}
                              style={{ fontSize: 11, padding: "2px 8px", textTransform: "none" }}
                            >
                              Copiar boleto
                            </Button>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                {sgpData.faturasAtrasadas?.length === 0 && (
                  <Box m={2} p={2} style={{ background: "#f0fdf4", borderRadius: 8, border: "1px solid #bbf7d0", textAlign: "center" }}>
                    <Typography style={{ fontSize: 13, color: "#166534", fontWeight: 600 }}>
                      ✅ Nenhuma fatura em atraso
                    </Typography>
                  </Box>
                )}

                {/* Ações */}
                <Box p={2} style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    className={classes.sgpActionBtn}
                    startIcon={<AssignmentIcon />}
                    onClick={() => setSgpChamadoOpen(true)}
                    style={{ backgroundColor: "#0891b2", color: "#fff" }}
                  >
                    Abrir Chamado
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    className={classes.sgpActionBtn}
                    startIcon={<PowerIcon />}
                    onClick={() => setSgpReligacaoOpen(true)}
                    style={{ backgroundColor: "#7c3aed", color: "#fff" }}
                  >
                    Religação de Confiança
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}
        {/* ── ABA Sys Clinica ── */}
        {tab === scTabIdx && (
          <Box className={classes.tabPanel} style={{ overflowY: "auto", paddingBottom: 16 }}>

            {/* Busca por CPF */}
            {!scPaciente && (
              <Box p={2}>
                <Typography style={{ fontSize: 12, fontWeight: 700, color: "#065f46", marginBottom: 8 }}>
                  Consultar paciente no Sys Clinica
                </Typography>
                <Box display="flex" style={{ gap: 8 }}>
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="CPF (só números)"
                    fullWidth
                    value={scCpf}
                    onChange={(e) => setScCpf(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && scBuscarPaciente()}
                    inputProps={{ maxLength: 14 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => scBuscarPaciente()}
                    disabled={scLoading}
                    style={{ backgroundColor: "#065f46", color: "#fff", minWidth: 44, padding: "0 10px" }}
                  >
                    {scLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon fontSize="small" />}
                  </Button>
                </Box>
                {scError && (
                  <Typography style={{ fontSize: 12, color: "#dc2626", marginTop: 8 }}>
                    {scError}
                  </Typography>
                )}
              </Box>
            )}

            {/* Card do Paciente */}
            {scPaciente && (
              <>
                <Box className={classes.scCard}>
                  <Box className={classes.scCardHeader}>
                    <LocalHospitalIcon style={{ color: "#065f46", fontSize: 18 }} />
                    <Box flex={1}>
                      <Typography style={{ fontWeight: 700, fontSize: 14, color: "#065f46" }}>
                        {scPaciente.nome}
                      </Typography>
                      <Typography style={{ fontSize: 11, color: "#4a5568" }}>
                        CPF: {scPaciente.cpf?.formatado || scPaciente.cpf}
                        {scPaciente.idade ? ` • ${scPaciente.idade} anos` : ""}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => { setScPaciente(null); setScCpf(""); setScError(""); }}>
                      <CloseIcon style={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                  <Box className={classes.scCardBody}>
                    {scPaciente.telefone && (
                      <Typography style={{ fontSize: 12, color: "#334155", marginBottom: 2 }}>
                        📞 {scPaciente.telefone}
                      </Typography>
                    )}
                    {scPaciente.sexo && (
                      <Typography style={{ fontSize: 12, color: "#334155", marginBottom: 2 }}>
                        {scPaciente.sexo}{scPaciente.tipo_sanguineo ? ` • ${scPaciente.tipo_sanguineo}` : ""}
                      </Typography>
                    )}
                    {scPaciente.convenio?.nome && (
                      <Typography style={{ fontSize: 12, color: "#334155", marginBottom: 2 }}>
                        🏥 Convênio: {scPaciente.convenio.nome}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Botões de ação */}
                <Box p={2} display="flex" flexDirection="column" style={{ gap: 8 }}>
                  {[
                    { tipo: "agendamentos", label: "Agendamentos",  icon: "📅", color: "#0369a1", perm: scPerm.verAgendamentos },
                    { tipo: "treinos",      label: "Treinos",        icon: "💪", color: "#7c3aed", perm: scPerm.verTreinos },
                    { tipo: "anamnese",     label: "Anamnese",       icon: "📋", color: "#b45309", perm: scPerm.verAnamnese },
                    { tipo: "historico",    label: "Histórico",      icon: "🕐", color: "#4a5568", perm: scPerm.verHistorico },
                    { tipo: "contas",       label: "Contas",         icon: "💰", color: "#b91c1c", perm: scPerm.verContas },
                    { tipo: "tratamentos",  label: "Tratamentos",    icon: "🩺", color: "#065f46", perm: scPerm.verTratamentos },
                  ].filter(b => b.perm).map(({ tipo, label, icon, color }) => (
                    <Button
                      key={tipo}
                      variant="contained"
                      fullWidth
                      className={classes.scActionBtn}
                      onClick={() => scAbrirModal(tipo)}
                      style={{ backgroundColor: color, color: "#fff" }}
                    >
                      {icon} {label}
                    </Button>
                  ))}
                </Box>
              </>
            )}
          </Box>
        )}
      </Drawer>

      {/* ── Modal: Abrir Chamado ── */}
      <Dialog open={sgpChamadoOpen} onClose={() => setSgpChamadoOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle style={{ paddingBottom: 8 }}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <AssignmentIcon style={{ color: "#0891b2" }} />
            Abrir Chamado SGP
          </Box>
        </DialogTitle>
        <DialogContent>
          {sgpData?.contratos?.length > 1 && (
            <TextField
              select
              label="Contrato"
              fullWidth
              size="small"
              variant="outlined"
              value={sgpChamadoContrato}
              onChange={(e) => setSgpChamadoContrato(e.target.value)}
              style={{ marginBottom: 12 }}
              SelectProps={{ native: true }}
            >
              {sgpData.contratos.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  #{c.id} — {c.status}
                </option>
              ))}
            </TextField>
          )}
          <TextField
            label="Descrição do problema"
            multiline
            minRows={4}
            fullWidth
            variant="outlined"
            size="small"
            value={sgpChamadoTexto}
            onChange={(e) => setSgpChamadoTexto(e.target.value)}
            placeholder="Descreva o problema relatado pelo cliente..."
            autoFocus
          />
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <Button onClick={() => setSgpChamadoOpen(false)} size="small" style={{ textTransform: "none" }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSgpAbrirChamado}
            disabled={sgpChamadoLoading || !sgpChamadoTexto.trim()}
            style={{ textTransform: "none" }}
          >
            {sgpChamadoLoading ? <CircularProgress size={16} color="inherit" /> : "Abrir Chamado"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Modal: Religação de Confiança ── */}
      <Dialog open={sgpReligacaoOpen} onClose={() => setSgpReligacaoOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle style={{ paddingBottom: 8 }}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <PowerIcon style={{ color: "#7c3aed" }} />
            Religação de Confiança
          </Box>
        </DialogTitle>
        <DialogContent>
          {sgpData?.contratos?.length > 1 && (
            <TextField
              select
              label="Contrato"
              fullWidth
              size="small"
              variant="outlined"
              value={sgpReligacaoContrato}
              onChange={(e) => setSgpReligacaoContrato(e.target.value)}
              style={{ marginBottom: 12 }}
              SelectProps={{ native: true }}
            >
              {sgpData.contratos.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  #{c.id} — {c.status}
                </option>
              ))}
            </TextField>
          )}
          <Box p={1.5} style={{ background: "#f3e8ff", borderRadius: 8, border: "1px solid #d8b4fe" }}>
            <Typography style={{ fontSize: 13, color: "#6d28d9", fontWeight: 600, marginBottom: 4 }}>
              ⚠️ Atenção
            </Typography>
            <Typography style={{ fontSize: 12, color: "#4c1d95" }}>
              Esta ação realiza uma liberação temporária de confiança no SGP para o contrato{" "}
              <strong>#{sgpReligacaoContrato}</strong>.
              O cliente voltará a ser bloqueado normalmente se não regularizar o pagamento.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <Button onClick={() => setSgpReligacaoOpen(false)} size="small" style={{ textTransform: "none" }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSgpReligacao}
            disabled={sgpReligacaoLoading}
            style={{ textTransform: "none", backgroundColor: "#7c3aed", color: "#fff" }}
          >
            {sgpReligacaoLoading
              ? <CircularProgress size={16} color="inherit" />
              : "Confirmar Religação"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* ── Modal: Novo/Editar Agendamento ── */}
      <Dialog open={!!scAgendForm} onClose={() => setScAgendForm(null)} fullWidth maxWidth="xs">
        <DialogTitle style={{ paddingBottom: 8 }}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <LocalHospitalIcon style={{ color: "#065f46" }} />
            {scAgendForm?.id ? "Editar Agendamento" : "Novo Agendamento"}
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField select label="Profissional" fullWidth size="small" variant="outlined" margin="dense"
            value={scAgendForm?.funcionario_id || ""}
            onChange={(e) => scOnFuncionarioChange(e.target.value)}>
            <option value="">Selecione...</option>
            {scFuncionarios.map(f => <option key={f.id} value={f.id}>{f.nome}{f.nivel ? ` — ${f.nivel}` : ""}</option>)}
          </TextField>
          <TextField select label="Serviço" fullWidth size="small" variant="outlined" margin="dense"
            value={scAgendForm?.servico_id || ""}
            onChange={(e) => { e.persist(); setScAgendForm(prev => ({ ...prev, servico_id: e.target.value })); }}
            disabled={scServicosLoading || !scAgendForm?.funcionario_id}>
            <option value="">Selecione o profissional primeiro</option>
            {scServicos.map(s => <option key={s.id} value={s.id}>{s.nome}{s.valor ? ` — R$ ${Number(s.valor).toFixed(2)}` : ""}</option>)}
          </TextField>
          <TextField label="Data" type="date" fullWidth size="small" variant="outlined" margin="dense"
            InputLabelProps={{ shrink: true }}
            value={scAgendForm?.data || ""}
            onChange={(e) => { e.persist(); setScAgendForm(prev => ({ ...prev, data: e.target.value })); }} />
          <TextField label="Hora" type="time" fullWidth size="small" variant="outlined" margin="dense"
            InputLabelProps={{ shrink: true }}
            value={scAgendForm?.hora || ""}
            onChange={(e) => { e.persist(); setScAgendForm(prev => ({ ...prev, hora: e.target.value })); }} />
          <TextField select label="Status" fullWidth size="small" variant="outlined" margin="dense"
            value={scAgendForm?.status || "Aguardando"}
            onChange={(e) => { e.persist(); setScAgendForm(prev => ({ ...prev, status: e.target.value })); }}>
            {["Aguardando", "Confirmado", "Cancelado", "Realizado"].map(s => <option key={s} value={s}>{s}</option>)}
          </TextField>
          <TextField select label="Pago" fullWidth size="small" variant="outlined" margin="dense"
            value={scAgendForm?.pago || "Não"}
            onChange={(e) => { e.persist(); setScAgendForm(prev => ({ ...prev, pago: e.target.value })); }}>
            <option value="Não">Não</option>
            <option value="Sim">Sim</option>
          </TextField>
          <TextField label="Observações" fullWidth size="small" variant="outlined" margin="dense" multiline minRows={2}
            value={scAgendForm?.obs || ""}
            onChange={(e) => { e.persist(); setScAgendForm(prev => ({ ...prev, obs: e.target.value })); }} />
        </DialogContent>
        <DialogActions style={{ padding: "10px 16px" }}>
          <Button onClick={() => setScAgendForm(null)} size="small" style={{ textTransform: "none" }}>Cancelar</Button>
          <Button variant="contained" size="small" onClick={scSalvarAgendamento} disabled={scAgendSaving}
            style={{ textTransform: "none", backgroundColor: "#065f46", color: "#fff" }}>
            {scAgendSaving ? <CircularProgress size={16} color="inherit" /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Modal: Editar Cronograma ── */}
      <Dialog open={!!scCronogramaForm} onClose={() => setScCronogramaForm(null)} fullWidth maxWidth="sm" scroll="paper">
        <DialogTitle style={{ paddingBottom: 8 }}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <LocalHospitalIcon style={{ color: "#7c3aed" }} />
            Editar Cronograma de Treinos
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TextField label="Nome do cronograma" fullWidth size="small" variant="outlined" margin="dense"
            value={scCronogramaForm?.nome || ""}
            onChange={(e) => { e.persist(); setScCronogramaForm(prev => ({ ...prev, nome: e.target.value })); }} />
          {SC_DIAS.map(dia => (
            <Box key={dia} style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 8, marginBottom: 8 }}>
              <Typography style={{ fontSize: 12, fontWeight: 700, color: "#4a5568", marginBottom: 4 }}>
                {SC_DIAS_LABEL[dia]}
              </Typography>
              <Box display="flex" style={{ gap: 8 }}>
                <TextField select fullWidth size="small" variant="outlined" label="Treino"
                  value={scCronogramaForm?.[dia]?.treino_id || ""}
                  onChange={(e) => { e.persist(); setScCronogramaForm(prev => ({ ...prev, [dia]: { ...prev[dia], treino_id: e.target.value || null } })); }}>
                  <option value="">Descanso</option>
                  {scTreinosLista.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                </TextField>
                <TextField size="small" variant="outlined" label="Obs" style={{ minWidth: 120 }}
                  value={scCronogramaForm?.[dia]?.obs || ""}
                  onChange={(e) => { e.persist(); setScCronogramaForm(prev => ({ ...prev, [dia]: { ...prev[dia], obs: e.target.value } })); }} />
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions style={{ padding: "10px 16px" }}>
          <Button onClick={() => setScCronogramaForm(null)} size="small" style={{ textTransform: "none" }}>Cancelar</Button>
          <Button variant="contained" size="small" onClick={scSalvarCronograma} disabled={scCronogramaSaving}
            style={{ textTransform: "none", backgroundColor: "#7c3aed", color: "#fff" }}>
            {scCronogramaSaving ? <CircularProgress size={16} color="inherit" /> : "Salvar Cronograma"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Modal Sys Clinica: dados dinâmicos ── */}
      <Dialog
        open={!!scModalType}
        onClose={() => { setScModalType(null); setScModalData(null); setScModalError(""); }}
        fullWidth
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle style={{ paddingBottom: 8 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <LocalHospitalIcon style={{ color: "#065f46" }} />
              <Typography style={{ fontWeight: 700 }}>
                {scModalType === "agendamentos" && "Agendamentos Futuros"}
                {scModalType === "historico" && "Histórico de Atendimentos"}
                {scModalType === "treinos" && "Treinos"}
                {scModalType === "anamnese" && "Anamnese"}
                {scModalType === "contas" && "Contas do Paciente"}
                {scModalType === "tratamentos" && "Tratamentos"}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => { setScModalType(null); setScModalData(null); }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {scModalLoading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress style={{ color: "#065f46" }} />
            </Box>
          )}
          {scModalError && !scModalLoading && (
            <Typography style={{ color: "#dc2626", fontSize: 13, padding: 8 }}>{scModalError}</Typography>
          )}
          {!scModalLoading && !scModalError && scModalData && (

            /* ── AGENDAMENTOS / HISTÓRICO ── */
            (scModalType === "agendamentos" || scModalType === "historico") ? (
              <>
                {scModalType === "agendamentos" && scPerm.editarAgendamentos && (
                  <Box mb={1} display="flex" justifyContent="flex-end">
                    <Button size="small" variant="contained"
                      style={{ backgroundColor: "#065f46", color: "#fff", textTransform: "none", fontSize: 12 }}
                      onClick={() => { setScModalType(null); scAbrirNovoAgendamento(); }}>
                      + Novo Agendamento
                    </Button>
                  </Box>
                )}
                {scModalData.agendamentos?.length > 0 ? scModalData.agendamentos.map((a) => (
                  <Box key={a.id} style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: 10, marginBottom: 10 }}>
                    <Typography style={{ fontWeight: 700, fontSize: 13 }}>
                      📅 {a.data ? moment(a.data).format("DD/MM/YYYY") : "—"} às {a.hora?.slice(0, 5) || "—"}
                    </Typography>
                    <Typography style={{ fontSize: 12, color: "#334155" }}>
                      {a.servico_nome} — {a.funcionario_nome}
                    </Typography>
                    <Typography style={{ fontSize: 12, color: "#4a5568" }}>
                      Status: <strong>{a.status}</strong> • Pago: {a.pago}
                      {a.retorno === "Sim" ? " • Retorno" : ""}
                    </Typography>
                    {a.obs && <Typography style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>Obs: {a.obs}</Typography>}
                    {scPerm.editarAgendamentos && (
                      <Box display="flex" style={{ gap: 6, marginTop: 6 }}>
                        <Button size="small" variant="outlined"
                          style={{ fontSize: 11, textTransform: "none", borderColor: "#0369a1", color: "#0369a1" }}
                          onClick={() => { setScModalType(null); scAbrirEditarAgendamento(a); }}>
                          Editar
                        </Button>
                        <Button size="small" variant="outlined"
                          style={{ fontSize: 11, textTransform: "none", borderColor: "#dc2626", color: "#dc2626" }}
                          onClick={() => scCancelarAgendamento(a.id)}>
                          Cancelar
                        </Button>
                      </Box>
                    )}
                  </Box>
                )) : (
                  <Typography style={{ fontSize: 13, color: "#6b7280" }}>Nenhum agendamento encontrado.</Typography>
                )}
              </>

            /* ── TREINOS ── */
            ) : scModalType === "treinos" ? (
              <>
                {scPerm.editarCronograma && (
                  <Box mb={1} display="flex" justifyContent="flex-end">
                    <Button size="small" variant="contained"
                      style={{ backgroundColor: "#7c3aed", color: "#fff", textTransform: "none", fontSize: 12 }}
                      onClick={() => { setScModalType(null); scAbrirCronogramaForm(); }}>
                      Editar Cronograma
                    </Button>
                  </Box>
                )}
                {scModalData.cronograma ? (
                  <>
                    <Typography style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
                      📋 {scModalData.cronograma.nome}
                    </Typography>
                    {scModalData.cronograma.dias?.map((d, i) => (
                      <Box key={i} style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: 10, marginBottom: 10 }}>
                        <Typography style={{ fontWeight: 700, fontSize: 13 }}>💪 {d.dia}</Typography>
                        <Typography style={{ fontSize: 12, color: "#065f46" }}>{d.treino?.nome}</Typography>
                        {d.treino?.exercicios?.map((ex) => (
                          <Typography key={ex.exercicio_id} style={{ fontSize: 12, color: "#334155", paddingLeft: 8 }}>
                            • {ex.nome} — {ex.instrucao}
                          </Typography>
                        ))}
                      </Box>
                    ))}
                  </>
                ) : (
                  <Typography style={{ fontSize: 13, color: "#6b7280" }}>Nenhum cronograma ativo. Clique em "Editar Cronograma" para criar.</Typography>
                )}
              </>

            /* ── ANAMNESE ── */
            ) : scModalType === "anamnese" ? (
              scModalData.anamnese ? (
                Object.entries(scModalData.anamnese.por_grupo || {}).map(([grupo, itens]) => (
                  <Box key={grupo} style={{ marginBottom: 12 }}>
                    <Typography style={{ fontWeight: 700, fontSize: 12, color: "#065f46", marginBottom: 4 }}>
                      {grupo}
                    </Typography>
                    {itens.map((item) => (
                      <Box key={item.id} style={{ paddingLeft: 8, marginBottom: 4 }}>
                        <Typography style={{ fontSize: 12, fontWeight: 600 }}>{item.item}</Typography>
                        {item.descricao && <Typography style={{ fontSize: 12, color: "#4a5568" }}>{item.descricao}</Typography>}
                      </Box>
                    ))}
                  </Box>
                ))
              ) : (
                <Typography style={{ fontSize: 13, color: "#6b7280" }}>Nenhuma anamnese cadastrada.</Typography>
              )

            /* ── CONTAS ── */
            ) : scModalType === "contas" ? (
              scModalData.contas?.length > 0 ? scModalData.contas.map((c) => (
                <Box key={c.id} style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: 10, marginBottom: 10 }}>
                  <Typography style={{ fontWeight: 700, fontSize: 13 }}>{c.descricao}</Typography>
                  <Typography style={{ fontSize: 12, color: "#334155" }}>
                    R$ {Number(c.valor).toFixed(2).replace(".", ",")} • Venc: {c.data_venc ? moment(c.data_venc).format("DD/MM/YYYY") : "—"}
                  </Typography>
                  <Typography style={{ fontSize: 12, fontWeight: 700, color: c.pago === "Sim" ? "#16a34a" : "#dc2626" }}>
                    {c.pago === "Sim" ? "✅ Pago" : "⚠️ Em aberto"}
                  </Typography>
                  {c.pago !== "Sim" && c.pix_copia_cola && (
                    <Button
                      size="small"
                      variant="outlined"
                      style={{ marginTop: 4, fontSize: 11 }}
                      onClick={() => { navigator.clipboard.writeText(c.pix_copia_cola); toast.success("PIX copiado!"); }}
                    >
                      Copiar PIX Copia e Cola
                    </Button>
                  )}
                  {c.pago !== "Sim" && c.asaas_link && (
                    <Button
                      size="small"
                      variant="outlined"
                      style={{ marginTop: 4, marginLeft: 4, fontSize: 11 }}
                      href={c.asaas_link}
                      target="_blank"
                      component="a"
                    >
                      Link de Pagamento
                    </Button>
                  )}
                </Box>
              )) : (
                <Typography style={{ fontSize: 13, color: "#6b7280" }}>Nenhuma conta encontrada.</Typography>
              )

            /* ── TRATAMENTOS ── */
            ) : scModalType === "tratamentos" ? (
              scModalData.tratamentos?.length > 0 ? scModalData.tratamentos.map((t) => (
                <Box key={t.id} style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: 10, marginBottom: 10 }}>
                  <Typography style={{ fontWeight: 700, fontSize: 13 }}>🩺 {t.procedimento_nome}</Typography>
                  <Typography style={{ fontSize: 12, color: "#334155" }}>
                    Profissional: {t.profissional_nome}
                  </Typography>
                  <Typography style={{ fontSize: 12, color: "#4a5568" }}>
                    {t.data_inicial ? moment(t.data_inicial).format("DD/MM/YYYY") : "—"} até {t.data_final ? moment(t.data_final).format("DD/MM/YYYY") : "—"}
                    {t.frequencia ? ` • ${t.frequencia}x/semana` : ""}
                  </Typography>
                  {t.horarios?.map((h, i) => (
                    <Typography key={i} style={{ fontSize: 12, color: "#6b7280", paddingLeft: 8 }}>
                      • {h.dia} às {h.hora?.slice(0, 5)}
                    </Typography>
                  ))}
                  <Typography style={{ fontSize: 12, fontWeight: 700, color: t.quitado === "Sim" ? "#16a34a" : "#f59e0b" }}>
                    {t.quitado === "Sim" ? "✅ Quitado" : "⚠️ Pendente"}
                  </Typography>
                </Box>
              )) : (
                <Typography style={{ fontSize: 13, color: "#6b7280" }}>Nenhum tratamento encontrado.</Typography>
              )
            ) : null
          )}
        </DialogContent>
        <DialogActions style={{ padding: "10px 16px" }}>
          <Button
            onClick={() => { setScModalType(null); setScModalData(null); setScModalError(""); }}
            size="small"
            style={{ textTransform: "none" }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
